import { useQueryClient } from "@tanstack/react-query";
import {
  JobAddedDocument,
  JobRemovedDocument,
  JobUpdatedDocument,
  type GetAllJobsForAllJobsPageQuery,
  type GetJobQuery,
  type GetJobsForRunSheetQuery,
  type GetMyJobsQuery,
  type JobAddedSubscription,
  type JobRemovedSubscription,
  type JobUpdatedSubscription,
} from "~/generated/graphql";
import { useSubscription } from "urql";
import { logger } from "../utils/logger";

export function useJobSubscriptions({
  runSheetIDs,
  assigneeID,
  queryFilter,
}: {
  runSheetIDs?: string[];
  assigneeID?: string;
  queryFilter?: any;
}) {
  const queryClient = useQueryClient();

  const handleJobAdded = (_: undefined, response: JobAddedSubscription) => {
    if (!response.jobAdded) return;

    const newJob = response.jobAdded;

    // Update runsheet cache if this subscription is for a runsheet
    if (runSheetIDs && runSheetIDs.length > 0) {
      runSheetIDs.forEach(runSheetID => {
        // Only add job to cache if it actually belongs to this runsheet
        if (newJob.runSheetID !== runSheetID) {
          return;
        }

        queryClient.setQueryData<GetJobsForRunSheetQuery | undefined>(
          ["GetJobsForRunSheet", { runSheetID }],
          oldData => {
            if (!oldData?.jobs?.items?.some(job => job._id === newJob._id)) {
              return {
                jobs: {
                  items: [...(oldData?.jobs?.items ?? []), newJob],
                },
              };
            }
            return oldData;
          },
        );
      });
    }

    if (queryFilter) {
      if (!jobMatchesFilter(newJob, queryFilter)) {
        return;
      }
      queryClient.setQueryData<GetAllJobsForAllJobsPageQuery | undefined>(
        ["GetAllJobsForAllJobsPage", { filter: queryFilter }],
        oldData => {
          if (!oldData?.jobs) return oldData;

          if (!oldData.jobs.items) {
            return {
              jobs: {
                ...oldData.jobs,
                items: [newJob],
              },
            };
          }

          // Don't add duplicates
          const jobExists = oldData.jobs.items.some(job => job._id === newJob._id);
          if (jobExists) return oldData;

          // Add new job
          return {
            jobs: {
              ...oldData.jobs,
              items: [...oldData.jobs.items, newJob],
            },
          };
        },
      );
    }

    // Update user caches for all assigned users
    if (newJob.assigneeIDs) {
      newJob.assigneeIDs.forEach(userId => {
        queryClient.setQueryData<GetMyJobsQuery | undefined>(["GetMyJobs", { userId }], oldData => {
          if (!oldData?.jobs?.items?.some(job => job._id === newJob._id)) {
            return {
              jobs: {
                items: [...(oldData?.jobs?.items ?? []), newJob],
              },
            };
          }
          return oldData;
        });
      });
    }
    return undefined;
  };

  const handleJobUpdated = (_: undefined, response: JobUpdatedSubscription) => {
    if (!response.jobUpdated) return;

    const updatedJob = response.jobUpdated;

    logger.log("Job Updated:", updatedJob._id);

    // Update individual job cache
    queryClient.setQueryData<GetJobQuery>(["GetJob", { id: updatedJob._id }], () => ({ job: updatedJob }));

    // Update runsheet cache
    if (runSheetIDs && runSheetIDs.length > 0) {
      runSheetIDs.forEach(runSheetID => {
        queryClient.setQueryData<GetJobsForRunSheetQuery | undefined>(
          ["GetJobsForRunSheet", { runSheetID }],
          oldData => {
            if (!oldData?.jobs?.items) {
              return { jobs: { items: [updatedJob] } };
            }

            const jobExists = oldData.jobs.items.some(job => job._id === updatedJob._id);
            if (jobExists) {
              return {
                jobs: {
                  items: oldData.jobs.items.map(job => (job._id === updatedJob._id ? updatedJob : job)),
                },
              };
            }
            return oldData;
          },
        );
      });
    }

    if (queryFilter) {
      const validFilterForOperationJobsPage = (queryFilter._operators?.runSheetID?.in?.length ?? 0) > 0;
      const validFilterForMyJobsJobTable = queryFilter.OR?.length > 0;
      const validFilterForMyJobsIssueSubTaskTable = queryFilter.AND?.length > 0;
      const hasValidFilter =
        validFilterForOperationJobsPage || validFilterForMyJobsJobTable || validFilterForMyJobsIssueSubTaskTable;

      if (!hasValidFilter) {
        return;
      }
      queryClient.setQueryData<GetAllJobsForAllJobsPageQuery | undefined>(
        ["GetAllJobsForAllJobsPage", { filter: queryFilter }],
        oldData => {
          if (!oldData?.jobs) return oldData;

          if (!oldData.jobs.items) {
            return {
              jobs: {
                ...oldData.jobs,
                items: [updatedJob],
              },
            };
          }

          const jobExists = oldData.jobs.items.some(job => job._id === updatedJob._id);

          const matchesFilter = jobMatchesFilter(updatedJob, queryFilter);

          if (jobExists && matchesFilter) {
            // Update existing job
            return {
              jobs: {
                ...oldData.jobs,
                items: oldData.jobs.items.map(job => (job._id === updatedJob._id ? updatedJob : job)),
              },
            };
          } else if (jobExists && !matchesFilter) {
            // Job no longer matches filter - remove it
            return {
              jobs: {
                ...oldData.jobs,
                items: oldData.jobs.items.filter(job => job._id !== updatedJob._id),
              },
            };
          } else if (!jobExists && matchesFilter) {
            // Job now matches filter - add it
            return {
              jobs: {
                ...oldData.jobs,
                items: [...oldData.jobs.items, updatedJob],
              },
            };
          }

          return oldData;
        },
      );
    }

    // Update ALL user caches that might be affected
    const allQueries = queryClient.getQueryCache().getAll();
    const myJobsQueries = allQueries.filter(query => query.queryKey[0] === "GetMyJobs");

    myJobsQueries.forEach(query => {
      const queryKey = query.queryKey as ["GetMyJobs", { userId: string }];
      const userId = queryKey[1]?.userId;

      if (!userId) return;

      queryClient.setQueryData<GetMyJobsQuery | undefined>(queryKey, oldData => {
        const isAssignedToUser = updatedJob.assigneeIDs?.includes(userId);

        if (!oldData?.jobs?.items) {
          return isAssignedToUser ? { jobs: { items: [updatedJob] } } : oldData;
        }

        const jobIndex = oldData.jobs.items.findIndex(job => job._id === updatedJob._id);
        const jobExistsInList = jobIndex !== -1;

        // Job in list and still assigned - update it
        if (jobExistsInList && isAssignedToUser) {
          return {
            jobs: {
              items: oldData.jobs.items.map(job => (job._id === updatedJob._id ? updatedJob : job)),
            },
          };
        }

        // Job in list but no longer assigned - remove it
        if (jobExistsInList && !isAssignedToUser) {
          return {
            jobs: {
              items: oldData.jobs.items.filter(job => job._id !== updatedJob._id),
            },
          };
        }

        // Job not in list but now assigned - add it
        if (!jobExistsInList && isAssignedToUser) {
          return {
            jobs: {
              items: [...oldData.jobs.items, updatedJob],
            },
          };
        }

        return oldData;
      });
    });
    return undefined;
  };

  const handleJobRemoved = (_: undefined, response: JobRemovedSubscription) => {
    if (!response.jobRemoved) return;

    const removedJob = response.jobRemoved;
    logger.log("Job Removed:", removedJob._id);

    // Remove from runsheet caches
    if (runSheetIDs && runSheetIDs.length > 0) {
      // Remove from individual runsheet caches
      runSheetIDs.forEach(runSheetID => {
        queryClient.setQueryData<GetJobsForRunSheetQuery | undefined>(
          ["GetJobsForRunSheet", { runSheetID }],
          oldData => {
            if (!oldData?.jobs?.items) return oldData;
            return {
              jobs: {
                items: oldData.jobs.items.filter(job => job._id !== removedJob._id),
              },
            };
          },
        );
      });
    }

    if (queryFilter) {
      queryClient.setQueryData<GetAllJobsForAllJobsPageQuery | undefined>(
        ["GetAllJobsForAllJobsPage", { filter: queryFilter }],
        oldData => {
          if (!oldData?.jobs) return oldData;
          if (!oldData.jobs.items) return oldData;

          return {
            jobs: {
              ...oldData.jobs,
              items: oldData.jobs.items.filter(job => job._id !== removedJob._id),
            },
          };
        },
      );
    }

    // Remove from user caches
    if (removedJob.assigneeIDs) {
      removedJob.assigneeIDs.forEach(userId => {
        queryClient.setQueryData<GetMyJobsQuery | undefined>(["GetMyJobs", { userId }], oldData => {
          if (!oldData?.jobs?.items) return oldData;
          return {
            jobs: {
              items: oldData.jobs.items.filter(job => job._id !== removedJob._id),
            },
          };
        });
      });
    }

    // Remove individual job cache
    queryClient.removeQueries({ queryKey: ["GetJob", { id: removedJob._id }] });
    return undefined;
  };

  const hasRunSheetIDs = runSheetIDs && runSheetIDs.length > 0;

  useSubscription(
    {
      query: JobAddedDocument,
      variables: hasRunSheetIDs ? { runSheetIDs } : { assigneeID },
      pause: !hasRunSheetIDs && !assigneeID,
    },
    handleJobAdded,
  );

  useSubscription(
    {
      query: JobUpdatedDocument,
      variables: hasRunSheetIDs ? { runSheetIDs } : { assigneeID },
      pause: !hasRunSheetIDs && !assigneeID,
    },
    handleJobUpdated,
  );

  useSubscription(
    {
      query: JobRemovedDocument,
      variables: hasRunSheetIDs ? { runSheetIDs } : { assigneeID },
      pause: !hasRunSheetIDs && !assigneeID,
    },
    handleJobRemoved,
  );
}

function jobMatchesFilter(job: any, filter: any): boolean {
  // Check parentID filter (for issue jobs)
  const parentIDFilter = filter?._operators?.parentID?.in;
  if (parentIDFilter && parentIDFilter.length > 0) {
    if (!job.parentID || !parentIDFilter.includes(job.parentID)) {
      return false;
    }
  }

  // Check assigneeIDs filter
  const assigneeIDsFilter = filter?._operators?.assigneeIDs?.in;
  if (assigneeIDsFilter && assigneeIDsFilter.length > 0) {
    const matchesAssignee = job.assigneeIDs?.some((id: string) => assigneeIDsFilter.includes(id));
    if (!matchesAssignee) {
      return false;
    }
  }

  // Check runSheetID filter (for runsheet-based queries)
  const runSheetIDFilter = filter?._operators?.runSheetID?.in;
  if (runSheetIDFilter && runSheetIDFilter.length > 0) {
    if (!job.runSheetID || !runSheetIDFilter.includes(job.runSheetID)) {
      return false;
    }
  }

  // Check for null runSheetID filter (for issue jobs)
  if (filter?.runSheetID === null && job.runSheetID !== null) {
    return false;
  }

  return true;
}
